import { SectionType } from "src/types";
import { getTrackingFromQueryString } from "src/utils/tracking";

import {
  URL_CHECKOUT,
  URL_CHECKOUT_DELIVERY,
  URL_CHECKOUT_PAYMENT,
  URL_ORDER_BY_CONDITION,
  URL_PRESCRIPTION_FLOW
} from "../constants/url.constants";

export const PageEvents = {
  name: {
    logIn: "Log In",
    forgotPassword: "Forgot Password",
    signUp: "Sign Up",
    plans: "Plans",
    accountSettings: "Account Settings",
    orderHistory: "Order History",
    wallet: "Wallet",
    gettingStarted: "Getting Started - {eval_name}",
    healthProfile: "Health Profile - {eval_name} - Step {number}",
    whyAsk: "Why Ask This Question?",
    planDetails: "Plan Details",
    faqs: "FAQs",
    shippingFrequency: "Shipping Frequency",
    refillNow: "Refill Now",
    shippingInformation: "Shipping Information",
    billingInformation: "Billing Information",
    selectPreferredMedication: "Select Preferred Medication",
    medicationDetails: "Medication Details",
    preferenceDetails: "Preference Details",
    deliveryDetails: "Delivery Details",
    paymentDetails: "Payment Details",
    shopHome: "Shop Home",
    yourCart: "Your Cart",
    category: "Category - {category_name}"
  },
  categories: {
    logIn: "Log In",
    signUp: "Sign Up",
    dashboard: "Dashboard",
    account: "Account",
    plans: "Plans",
    evaluation: "Evaluation",
    healthProfile: "Health Profile",
    preferredMedication: "Preferred Medication",
    delivery: "Delivery",
    payment: "Payment",
    shop: "Shop",
    checkout: "Checkout"
  }
};

export const TRACKING_EVENTS_2 = {
  signedIn: "Signed In",
  signedUp: "Signed Up",
  signedOut: "Signed Out",
  evaluationStarted: "Evaluation Started",
  evaluationUpdated: "Evaluation Updated",
  evaluationCompleted: "Evaluation Completed",
  productAdded: "Product Added",
  productRemoved: "Product Removed",
  couponApplied: "Coupon Applied",
  couponRemoved: "Coupon Removed",
  preferredMedicationSkipped: "Preferred Medication Skipped",
  checkoutStarted: "Checkout Started",
  checkoutCompleted: "Checkout Completed",
  paymentEntered: "Payment Info Entered",
  prescriptionRefillActivated: "Prescription Refill Activated",
  prescriptionRefillDeactivated: "Prescription Refill Deactivated",
  shippingFrequencyUpdated: "Shipping Frequency Updated",
  monthlyDoseUpdated: "Monthly Dose Updated",
  connectWithDoctorInitiated: "Connect With Doctor Initiated",
  profileDetailsUpdated: "Profile Details Updated",
  billingDetailsUpdated: "Billing Details Updated",
  shippingDetailsUpdated: "Shipping Details Updated",
  orderCompleted: "Order Completed",
  orderCancelled: "Order Cancelled",
  orderRefunded: "Order Refunded",
  doctorApproved: "Doctor Approved",
  doctorRejected: "Doctor Rejected"
};

export const TRACKING_EVENTS = {
  LOGIN: "User logs in",
  ORDER_COMPLETED: "Order Completed",
  COMPLETE_REGISTRATION: "Complete Registration",
  EVALUATION: "Evaluation",
  VIEW_CHECKOUT_PAGE: "View Checkout Page",
  COMPLETE_PURCHASE: "Complete Purchase",
  ADD_ITEM_TO_CART: "Add Item To Cart",
  EDIT_SUBSCRIPTION: "Edit Subscription",
  VIEW_DETAIL: "View Item Detail Page",
  START_EVALUATION: "Start Evaluation",
  UPDATE_EVALUATION: "Update Evaluation",
  CONTINUE_EVALUATION: "Continue Evaluation",
  COMPLETE_EVAL: "Complete Evaluation",
  SEARCH: "Search",
  SKIP_MED: "Skip Med"
};

export const PAGE_VIEW_TRACKINGS = {
  MEDS_SELECTION: "View Page: Start Meds Selection",
  VIEW_MEDS: "View Page: View Meds",
  TREATMENT_SUMMARY: "View Page: Treatment summary",
  SELECT_FREQUENCY: "View Page: Select Frequency",
  SELECT_DOSAGE: "View Page: Select Dosage",
  CONFIRM_ORDER: "View Page: Confirm Your Order",
  DELIVERY: "View Page: Delivery",
  PAYMENT: "View Page: Payment Options"
};

export const PRESCRIPTION_FLOW_REGEX = [
  {
    regex: /^\/c\/.*\/payment.*$/,
    tracking: PAGE_VIEW_TRACKINGS.PAYMENT
  },
  {
    regex: /^\/c\/.*\/delivery.*$/,
    tracking: PAGE_VIEW_TRACKINGS.DELIVERY
  },
  {
    regex: /^\/c\/[^/]+\/price.*variant=frequency$/,
    tracking: PAGE_VIEW_TRACKINGS.SELECT_FREQUENCY
  },
  {
    regex: /^\/c\/[^/]+\/price.*variant=dosage$/,
    tracking: PAGE_VIEW_TRACKINGS.SELECT_DOSAGE
  },
  {
    regex: /^\/c\/.+\/price.+$/,
    tracking: PAGE_VIEW_TRACKINGS.TREATMENT_SUMMARY
  },
  {
    regex: /^\/c\/.+$/,
    tracking: PAGE_VIEW_TRACKINGS.MEDS_SELECTION
  }
];

export const getViewPageEventName = (pageName?: string) => {
  if (pageName) {
    switch (pageName) {
      case "login":
        return "View Page: Login";
      case "register":
        return "View Page: Create an Account";
      case "forgot_password":
        return "View Page: Reset Password";
      default:
        return `View Page: ${pageName}`;
    }
  }
  const path = getPath();
  if (path.startsWith(URL_ORDER_BY_CONDITION)) {
    return orderByConditionCase();
  }

  if (path.startsWith(`${URL_PRESCRIPTION_FLOW}/`)) {
    const pathAndQueryParams = path + window.location.search;

    const result = PRESCRIPTION_FLOW_REGEX.find(({ regex }) => regex.test(pathAndQueryParams));
    return result?.tracking || "View Page";
  }

  switch (path) {
    case URL_CHECKOUT:
      return PAGE_VIEW_TRACKINGS.CONFIRM_ORDER;
    case URL_CHECKOUT_PAYMENT:
      return PAGE_VIEW_TRACKINGS.PAYMENT;
    case URL_CHECKOUT_DELIVERY:
      return PAGE_VIEW_TRACKINGS.DELIVERY;
    default:
      return `View Page`;
  }
};

const orderByConditionCase = () => {
  const section = getTrackingFromQueryString("section");
  switch (section) {
    case SectionType.medsSelection:
      return PAGE_VIEW_TRACKINGS.MEDS_SELECTION;
    case SectionType.productSelection:
      return PAGE_VIEW_TRACKINGS.VIEW_MEDS;
    case SectionType.treatmentSummary:
      return PAGE_VIEW_TRACKINGS.TREATMENT_SUMMARY;
    case SectionType.freqSelection:
      return PAGE_VIEW_TRACKINGS.SELECT_FREQUENCY;
    case SectionType.dosageSelection:
      return PAGE_VIEW_TRACKINGS.SELECT_DOSAGE;
    default:
      return `View Page: ${section}`;
  }
};

const getPath = () => {
  let path = window.location.pathname;
  const prefixList = ["/en/", "/zh/", "/ja/", "/hi/"];
  prefixList.forEach((prefix) => {
    if (path.startsWith(prefix)) {
      path = path.slice(prefix.length - 1);
    }
  });

  return path;
};
